export interface TranscriptTurn {
  /** Display label for the speaker, e.g. "User", "Agent", "System". */
  role: string
  content: string
}

/** Matches a leading "Role: text" prefix at the start of a line. */
const ROLE_LINE = /^([A-Za-z][A-Za-z ]*?):\s?(.*)$/

/** Normalizes API message roles to the labels used in the transcript UI. */
function normalizeRole(role: string): string {
  switch (role.trim().toLowerCase()) {
    case "user":
      return "User"
    case "assistant":
      return "Agent"
    case "system":
      return "System"
    default:
      return role.trim()
  }
}

/**
 * Parses a transcript test case's `input_str` into readable turns.
 *
 * Handles both formats produced by the app:
 *  - JSON array of `{ role, content }` messages (deep-dive "create from result")
 *  - Plain "Role: text" lines (manual transcript modal / CSV upload)
 *
 * Returns `null` when the input is empty or not recognizable as a transcript,
 * so callers can fall back to plain rendering.
 */
export function parseTranscriptTurns(
  input: string | null | undefined,
): TranscriptTurn[] | null {
  const text = input?.trim()
  if (!text) {
    return null
  }

  // Format 1: JSON array of message objects.
  try {
    const parsed: unknown = JSON.parse(text)
    if (Array.isArray(parsed)) {
      const turns: TranscriptTurn[] = []
      for (const item of parsed) {
        if (
          typeof item === "object" &&
          item !== null &&
          "role" in item &&
          "content" in item &&
          typeof (item as { content: unknown }).content === "string"
        ) {
          turns.push({
            role: normalizeRole(String((item as { role: unknown }).role)),
            content: (item as { content: string }).content,
          })
        } else {
          // Not a clean message array — let the caller render it raw.
          return null
        }
      }
      return turns.length > 0 ? turns : null
    }
  } catch {
    // Not JSON — fall through to plain-text parsing.
  }

  // Format 2: "Role: text" lines, with continuation lines folded into the
  // preceding turn so multi-line messages stay intact.
  const turns: TranscriptTurn[] = []
  for (const line of text.split("\n")) {
    const match = line.match(ROLE_LINE)
    if (match) {
      turns.push({ role: match[1].trim(), content: match[2] })
    } else if (turns.length > 0) {
      turns[turns.length - 1].content += `\n${line}`
    } else {
      // Content before any role label — not a recognizable transcript.
      return null
    }
  }

  return turns.length > 0 ? turns : null
}
